import { api } from '@/config/api';

export interface MetaAdAccount {
  accountUid: string; companyUid: string;
  appId: string;
  apiBaseUrl: string; apiVersion: string;
  adAccountId: string;
  adAccountName: string;
  businessManagerId?: string;
  pageId?: string;
  pageIds?: string[];
  instagramAccountId?: string;
  facebookUserName?: string;
  facebookUserId?: string;
  currency: string;
  timezoneName?: string;
  // WhatsApp Business Account (for CTWA ads + Cloud API messaging)
  whatsappBusinessAccountId?: string;
  whatsappPhoneNumberId?: string;
  whatsappDisplayPhoneNumber?: string;
  whatsappVerifiedName?: string;
  whatsappQualityRating?: string;
  whatsappCodeVerificationStatus?: string;
  whatsappLinkedPageIds?: string[];
  whatsappLinkCheckedAt?: string;
  // Cached health
  accountStatus?: number;
  disableReason?: number;
  amountSpent?: string;
  balance?: string;
  spendCap?: string;
  age?: number;
  rateLimitUsage?: Record<string, unknown>;
  healthFetchedAt?: string;
  // OAuth token lifecycle (only populated when account was connected via OAuth)
  oauthGrantedAt?: string;
  /** Scopes the user actually granted in the OAuth dialog. May be a subset of
   *  what we requested if the user unticked anything. Used by MetaScopeBanner
   *  to detect missing-scope state and prompt reconnect. */
  oauthScopes?: string[];
  tokenExpiresAt?: string;
  tokenLastRefreshedAt?: string;
  tokenRefreshError?: string;
  isDefault: boolean; isActive: boolean;
  createdAt: string; updatedAt: string;
}

export interface WhatsAppPhone {
  id: string;
  displayPhoneNumber: string;
  verifiedName?: string;
  qualityRating?: string;
  codeVerificationStatus?: string;
  platformType?: string;
}

export interface WhatsAppOption {
  wabaId: string;
  wabaName?: string;
  businessId?: string;
  businessName?: string;
  ownership: 'owned' | 'client';
  phones: WhatsAppPhone[];
}

export type WhatsAppProbeResponse =
  | { requiresChoice: true; available: WhatsAppOption[] }
  | { requiresChoice: false; account: MetaAdAccount; selected?: { wabaId: string; phone: WhatsAppPhone; businessId?: string } };

export interface AdAccountHealth {
  accountStatus?: number;
  disableReason?: number;
  amountSpent?: string;
  balance?: string;
  spendCap?: string;
  currency?: string;
  timezoneName?: string;
  age?: number;
  rateLimitUsage?: Record<string, unknown>;
  fetchedAt: string;
  staleSince?: string;
  checkedAt?: string;
  verified?: boolean;
  error?: string;
  needsReconnect?: boolean;
}

export const facebookAdAccountService = {
  get: async (): Promise<MetaAdAccount> => (await api.get('/slighter_crm/facebook/ad-account')).data,
  update: async (p: Partial<MetaAdAccount>): Promise<MetaAdAccount> =>
    (await api.patch('/slighter_crm/facebook/ad-account', p)).data,
  health: async (refresh = false): Promise<AdAccountHealth> =>
    (await api.get('/slighter_crm/facebook/ad-account/health', { params: { refresh: refresh ? '1' : undefined } })).data,

  setSpendCap: async (body: { spendCap: number; resetPeriod?: 'lifetime' | 'monthly' }): Promise<{ account: MetaAdAccount; spendCap: string }> =>
    (await api.post('/slighter_crm/facebook/ad-account/spend-cap', body)).data,
  oauthStart: async (): Promise<{ url: string }> => (await api.get('/slighter_crm/facebook/oauth/start', { withCredentials: true })).data,

  list: async (): Promise<MetaAdAccount[]> => (await api.get('/slighter_crm/facebook/ad-account/list')).data,
  setDefault: async (accountUid: string): Promise<MetaAdAccount> =>
    (await api.post(`/slighter_crm/facebook/ad-account/${accountUid}/set-default`)).data,
  setActive: async (accountUid: string, isActive: boolean): Promise<MetaAdAccount> =>
    (await api.patch(`/slighter_crm/facebook/ad-account/${accountUid}/active`, { isActive })).data,
  remove: async (accountUid: string): Promise<{ ok: true }> =>
    (await api.delete(`/slighter_crm/facebook/ad-account/${accountUid}`)).data,

  syncResources: async (
    accountUid: string,
    pageId?: string,
  ): Promise<SyncResourcesResponse> =>
    (await api.post(`/slighter_crm/facebook/ad-account/${accountUid}/sync-resources`, pageId ? { pageId } : {})).data,

  /**
   * Probe Meta for WhatsApp Business Accounts. Pass no args to auto-select
   * when there's one obvious choice, or `{ wabaId, phoneNumberId }` to commit.
   */
  probeWhatsApp: async (
    accountUid: string,
    body: { wabaId?: string; phoneNumberId?: string } = {},
  ): Promise<WhatsAppProbeResponse> =>
    (await api.post(`/slighter_crm/facebook/ad-account/${accountUid}/whatsapp/probe`, body)).data,

  clearWhatsApp: async (accountUid: string): Promise<MetaAdAccount> =>
    (await api.delete(`/slighter_crm/facebook/ad-account/${accountUid}/whatsapp`)).data,

  /** Link an Instagram professional account to the default ad account — what the /instagram module reads. */
  connectInstagram: async (instagramAccountId: string): Promise<MetaAdAccount> =>
    (await api.post('/slighter_crm/facebook/ad-account/instagram', { instagramAccountId })).data,
  disconnectInstagram: async (): Promise<MetaAdAccount> =>
    (await api.delete('/slighter_crm/facebook/ad-account/instagram')).data,
};

export interface SyncablePage {
  id: string;
  name: string;
  pictureUrl?: string;
  instagramAccount: { id: string; username?: string; profilePictureUrl?: string } | null;
}

export type SyncResourcesResponse =
  | { requiresChoice: true; availablePages: SyncablePage[] }
  | { requiresChoice: false; account: MetaAdAccount; selectedPage?: SyncablePage; businessManagerId?: string };
